import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Archive, Folder, Pause, Play, Save, Trash2 } from 'lucide-react';
import kgDatasetService from '../../../services/kgDatasetService';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './KgSettingsTab.module.scss';
import {
  DATASET_STATUS,
  activateBlockedReason,
  canActivate,
  describeError,
  formatNumber,
  recordCount,
  recordNoun,
} from '../kgUtils';

/**
 * Settings (screen 9) — rename, status, delete.
 *
 * Two things carry real weight here:
 *
 *   • Pause is a kill switch, not a delete. Framing matters: it stops agents
 *     answering without losing anything, and browsing keeps working.
 *   • Delete is owner-only, synchronous, and scales at roughly 31 seconds per
 *     MILLION records. So: confirm by typing the key, block the UI while it runs,
 *     and use the service's five-minute timeout rather than the shared 30s one.
 */
const KgSettingsTab = ({ dataset, onMutated, notify, onSetStatus, statusBusy }) => {
  const navigate = useNavigate();

  const [label, setLabel] = useState(dataset.label || '');
  const [description, setDescription] = useState(dataset.description || '');
  const [savingDetails, setSavingDetails] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const records = recordCount(dataset);
  const detailsDirty =
    label !== (dataset.label || '') || description !== (dataset.description || '');
  const blockedReason = activateBlockedReason(dataset);
  // ~31s per million records, and above a couple of million it can outrun the
  // database's own statement timeout regardless of what the client waits.
  const slowDelete = records > 500000;

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    try {
      await kgDatasetService.updateDataset(dataset.id, { label, description });
      notify('Saved.', 'success');
      await onMutated();
    } catch (e) {
      notify(describeError(e, "Couldn't save those details."), 'error');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await kgDatasetService.deleteDataset(dataset.id);
      notify(`"${dataset.label}" and everything in it has been deleted.`, 'info');
      navigate('/knowledge-graph');
    } catch (e) {
      setDeleteError(describeError(e, "Couldn't delete this dataset."));
      setDeleting(false);
    }
  };

  return (
    <div className={styles.wrap}>
      {/* ── Details ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Details</h3>
        <TextField
          size="small"
          label="Name"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          fullWidth
        />
        <TextField
          size="small"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
        <TextField
          size="small"
          label="Key"
          value={dataset.key}
          fullWidth
          disabled
          helperText="Fixed after creation — it is part of the push URL."
          InputProps={{ className: styles.monoInput }}
        />
        <div className={styles.actions}>
          <Button
            variant="contained"
            size="small"
            className={sharedStyles.primaryBtn}
            startIcon={savingDetails ? <CircularProgress size={13} /> : <Save size={14} />}
            onClick={handleSaveDetails}
            disabled={!detailsDirty || savingDetails}
          >
            Save
          </Button>
        </div>
      </section>

      {/* ── Availability ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Availability to agents</h3>
        <p className={styles.sectionHelp}>
          Currently <strong>{DATASET_STATUS[dataset.status]?.label || dataset.status}</strong> —{' '}
          {DATASET_STATUS[dataset.status]?.meaning}
        </p>

        <div className={styles.statusButtons}>
          {dataset.status !== 'active' && (
            <Tooltip title={blockedReason || ''}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  className={sharedStyles.primaryBtn}
                  startIcon={<Play size={14} />}
                  onClick={() => onSetStatus('active')}
                  disabled={statusBusy || !canActivate(dataset)}
                >
                  Activate
                </Button>
              </span>
            </Tooltip>
          )}
          {dataset.status === 'active' && (
            <Button
              variant="outlined"
              size="small"
              className={sharedStyles.outlineBtn}
              startIcon={<Pause size={14} />}
              onClick={() => onSetStatus('paused')}
              disabled={statusBusy}
            >
              Pause
            </Button>
          )}
          {dataset.status !== 'archived' && (
            <Button
              variant="outlined"
              size="small"
              className={sharedStyles.subtleBtn}
              startIcon={<Archive size={14} />}
              onClick={() => onSetStatus('archived')}
              disabled={statusBusy}
            >
              Archive
            </Button>
          )}
        </div>

        {!canActivate(dataset) && (
          <Alert severity="info" className={styles.inlineAlert}>
            {blockedReason}
          </Alert>
        )}
      </section>

      {/* ── Attaching it to an agent ── */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Using this from an agent</h3>
        <p className={styles.sectionHelp}>
          This dataset owns its own knowledge folder. Turn on <strong>Knowledge Graph</strong> in
          an agent’s features and pick this dataset — the folder is attached for you.
        </p>
        <div className={styles.folderRow}>
          <Folder size={15} />
          <span>
            Folder <code>#{dataset.folder_id}</code> — created with this dataset. Don’t upload
            files into it; its contents come from pushed records.
          </span>
        </div>
        {dataset.status !== 'active' && (
          <Alert severity="warning" className={styles.inlineAlert}>
            While this dataset is {DATASET_STATUS[dataset.status]?.label.toLowerCase()}, an agent
            with the folder attached still gets no knowledge-graph tools.
          </Alert>
        )}
      </section>

      {/* ── Danger zone ── */}
      <section className={`${styles.section} ${styles.danger}`}>
        <h3 className={styles.sectionTitle}>Delete this dataset</h3>
        <p className={styles.sectionHelp}>
          Removes the {formatNumber(records)} {recordNoun(dataset, records)}, the facets, the
          folder and the embeddings. Only the owner can do this, and it cannot be undone.
        </p>
        <div className={styles.actions}>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<Trash2 size={14} />}
            onClick={() => {
              setConfirmText('');
              setDeleteError(null);
              setDeleteOpen(true);
            }}
          >
            Delete dataset
          </Button>
        </div>
      </section>

      <Dialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete “{dataset.label}”?</DialogTitle>
        <DialogContent className={styles.confirmBody}>
          <Typography variant="body2">
            This permanently removes {formatNumber(records)}{' '}
            {recordNoun(dataset, records)}, the facet catalog, the folder and every embedding.
            Any agent using it will lose its knowledge-graph tools.
          </Typography>

          {slowDelete && (
            <Alert severity="warning">
              A dataset this size takes a while to delete — roughly half a minute per million
              records — and the page will stay busy until it finishes.
            </Alert>
          )}

          <Typography variant="body2">
            Type <code className={styles.confirmKey}>{dataset.key}</code> to confirm.
          </Typography>
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={deleting}
            InputProps={{ className: styles.monoInput }}
          />

          {deleting && (
            <>
              <LinearProgress />
              <Typography variant="caption" className={sharedStyles.muted}>
                Deleting — don’t close this tab.
              </Typography>
            </>
          )}

          {deleteError && <Alert severity="error">{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting || confirmText !== dataset.key}
            startIcon={deleting ? <CircularProgress size={13} /> : <Trash2 size={14} />}
          >
            {deleting ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default KgSettingsTab;
